import { useState, useEffect, useMemo } from "react";
import { post, get } from "../api";
import { useTheme } from "../ThemeContext";

const fmt = (v) => "R$ " + Number(v || 0).toFixed(2).replace(".", ",");
const pvFloat = (s) => {
  if (typeof s === "number") return s;
  if (!s) return 0;
  const v = parseFloat(String(s).replace(",", "."));
  return isNaN(v) ? 0 : v;
};
const fmtData = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function ReajusteMassa({ lojaId, produtos, categorias, subcategorias, aberto, onFechar, onSucesso }) {
  const { theme: C } = useTheme();
  const [step, setStep] = useState(1); // 1=filtro, 2=reajuste, 3=preview
  const [aba, setAba] = useState("novo"); // novo | historico

  // ── PASSO 1: FILTROS ──
  const [escopoTipo, setEscopoTipo] = useState("todos"); // todos | categoria | subcategoria | faixa | manual
  const [categoriaId, setCategoriaId] = useState("");
  const [subcategoriaId, setSubcategoriaId] = useState("");
  const [precoMin, setPrecoMin] = useState("");
  const [precoMax, setPrecoMax] = useState("");
  const [idsSelecionados, setIdsSelecionados] = useState([]);
  const [buscaManual, setBuscaManual] = useState("");

  // ── PASSO 2: PARÂMETROS DE REAJUSTE ──
  const [tipo, setTipo] = useState("percentual"); // percentual | fixo
  const [operacao, setOperacao] = useState("+"); // + | -
  const [valor, setValor] = useState("");
  const [aplicarEm, setAplicarEm] = useState("preco"); // preco | promocao | ambos
  const [arredondamento, setArredondamento] = useState("none"); // none | 50 | 90 | 99 | int

  // ── PASSO 3: PREVIEW ──
  const [preview, setPreview] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [toast, setToast] = useState(null);

  // ── HISTÓRICO ──
  const [historico, setHistorico] = useState([]);
  const [carregandoHist, setCarregandoHist] = useState(false);

  const notify = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    if (aberto) {
      setStep(1); setAba("novo");
      setEscopoTipo("todos"); setCategoriaId(""); setSubcategoriaId("");
      setPrecoMin(""); setPrecoMax(""); setIdsSelecionados([]); setBuscaManual("");
      setTipo("percentual"); setOperacao("+"); setValor(""); setAplicarEm("preco"); setArredondamento("none");
      setPreview(null);
    }
  }, [aberto]);

  useEffect(() => {
    if (aberto && aba === "historico") carregarHistorico();
  }, [aberto, aba]);

  const carregarHistorico = async () => {
    setCarregandoHist(true);
    try {
      const r = await post("produtos", { acao: "reajuste_historico", loja_id: lojaId });
      if (r.ok) setHistorico(r.dados || []);
    } catch (e) { notify("Erro ao carregar histórico", false); }
    setCarregandoHist(false);
  };

  const reverter = async (logId) => {
    if (!confirm("Reverter este reajuste? Os preços vão voltar ao que eram antes.")) return;
    try {
      const r = await post("produtos", { acao: "reajuste_reverter", loja_id: lojaId, log_id: logId });
      if (r.ok) {
        notify(`✓ ${r.revertidos} produtos restaurados`);
        carregarHistorico();
        onSucesso && onSucesso();
      } else {
        notify(r.error || "Erro ao reverter", false);
      }
    } catch (e) { notify("Erro de conexão", false); }
  };

  // Filtros computados
  const subsDaCategoria = useMemo(() => {
    if (!categoriaId) return [];
    return (subcategorias || []).filter(s => String(s.categoria_id) === String(categoriaId));
  }, [categoriaId, subcategorias]);

  const produtosFiltrados = useMemo(() => {
    let lista = produtos || [];
    if (escopoTipo === "categoria" && categoriaId) lista = lista.filter(p => String(p.categoria_id) === String(categoriaId));
    if (escopoTipo === "subcategoria" && subcategoriaId) lista = lista.filter(p => String(p.subcategoria_id) === String(subcategoriaId));
    if (escopoTipo === "faixa") {
      const mn = pvFloat(precoMin), mx = pvFloat(precoMax);
      if (mn > 0) lista = lista.filter(p => parseFloat(p.preco) >= mn);
      if (mx > 0) lista = lista.filter(p => parseFloat(p.preco) <= mx);
    }
    if (escopoTipo === "manual") {
      lista = lista.filter(p => idsSelecionados.includes(p.id));
    }
    return lista;
  }, [produtos, escopoTipo, categoriaId, subcategoriaId, precoMin, precoMax, idsSelecionados]);

  const produtosDisponiveisManual = useMemo(() => {
    let lista = produtos || [];
    if (buscaManual.trim()) {
      const q = buscaManual.toLowerCase();
      lista = lista.filter(p => (p.nome || "").toLowerCase().includes(q));
    }
    return lista;
  }, [produtos, buscaManual]);

  const escopoDescricao = useMemo(() => {
    if (escopoTipo === "todos") return "Todos os produtos";
    if (escopoTipo === "categoria") {
      const c = (categorias || []).find(x => String(x.id) === String(categoriaId));
      return "Categoria: " + (c?.nome || "?");
    }
    if (escopoTipo === "subcategoria") {
      const s = (subcategorias || []).find(x => String(x.id) === String(subcategoriaId));
      return "Subcategoria: " + (s?.nome || "?");
    }
    if (escopoTipo === "faixa") return `Faixa de R$ ${precoMin || 0} a R$ ${precoMax || "∞"}`;
    if (escopoTipo === "manual") return `${idsSelecionados.length} produtos selecionados`;
    return "—";
  }, [escopoTipo, categoriaId, subcategoriaId, precoMin, precoMax, idsSelecionados, categorias, subcategorias]);

  const montarFiltros = () => {
    const f = {};
    if (escopoTipo === "categoria") f.categoria_id = categoriaId;
    if (escopoTipo === "subcategoria") f.subcategoria_id = subcategoriaId;
    if (escopoTipo === "faixa") {
      if (precoMin) f.preco_min = pvFloat(precoMin);
      if (precoMax) f.preco_max = pvFloat(precoMax);
    }
    if (escopoTipo === "manual") f.ids = idsSelecionados;
    return f;
  };

  const irPreview = async () => {
    const v = pvFloat(valor);
    if (v <= 0) { notify("Digite um valor válido", false); return; }
    if (escopoTipo === "manual" && idsSelecionados.length === 0) { notify("Selecione ao menos um produto", false); return; }
    if (produtosFiltrados.length === 0) { notify("Nenhum produto encontrado com os filtros", false); return; }
    setCarregando(true);
    try {
      const r = await post("produtos", {
        acao: "reajuste_preview", loja_id: lojaId,
        tipo, operacao, valor: v, aplicar_em: aplicarEm, arredondamento,
        filtros: montarFiltros()
      });
      if (r.ok) { setPreview(r.preview || []); setStep(3); }
      else notify(r.error || "Erro ao gerar preview", false);
    } catch (e) { notify("Erro de conexão", false); }
    setCarregando(false);
  };

  const aplicar = async () => {
    const afetados = (preview || []).filter(p => p.mudou).length;
    if (!confirm(`Aplicar reajuste em ${afetados} produto(s)?\n\nEscopo: ${escopoDescricao}\nReajuste: ${operacao}${valor}${tipo === "percentual" ? "%" : " R$"}\n\nVocê pode reverter no histórico depois.`)) return;
    setAplicando(true);
    try {
      const r = await post("produtos", {
        acao: "reajuste_aplicar", loja_id: lojaId,
        tipo, operacao, valor: pvFloat(valor), aplicar_em: aplicarEm, arredondamento,
        filtros: montarFiltros(), escopo_descricao: escopoDescricao
      });
      if (r.ok) {
        notify(`✓ ${r.mensagem}`);
        onSucesso && onSucesso();
        setTimeout(() => onFechar && onFechar(), 1200);
      } else {
        notify(r.error || "Erro ao aplicar", false);
      }
    } catch (e) { notify("Erro de conexão", false); }
    setAplicando(false);
  };

  if (!aberto) return null;

  // ── ESTILOS ──
  const sInput = { padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };
  const sBtn = (bg, fg = "#fff") => ({ padding: "10px 16px", background: bg, color: fg, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" });
  const sBtnGhost = { padding: "10px 16px", background: "transparent", color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" };
  const sLabel = { fontSize: 11, color: C.textMuted, fontWeight: 700, marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: 0.5 };
  const sChip = (ativo, cor = "#3B82F6") => ({ padding: "8px 14px", borderRadius: 8, border: `1px solid ${ativo ? cor : C.border}`, background: ativo ? cor + "20" : "transparent", color: ativo ? cor : C.text, fontSize: 13, fontWeight: 700, cursor: "pointer", flex: 1, textAlign: "center" });

  return (
    <div onClick={() => !aplicando && onFechar && onFechar()} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(6px)" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 16, padding: 24, width: "100%", maxWidth: 800, maxHeight: "90vh", display: "flex", flexDirection: "column", border: `1px solid ${C.border}` }}>

        {toast && <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 10000, background: toast.ok ? "#22C55E" : "#EF4444", color: "#fff", padding: "10px 22px", borderRadius: 10, fontWeight: 700, fontSize: 13 }}>{toast.msg}</div>}

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: C.text, display: "flex", alignItems: "center", gap: 10 }}>
            ⚡ Reajuste em Massa
          </div>
          <div onClick={() => !aplicando && onFechar && onFechar()} style={{ cursor: "pointer", fontSize: 22, color: C.textMuted, padding: "0 8px" }}>✕</div>
        </div>

        {/* TABS */}
        <div style={{ display: "flex", gap: 8, marginBottom: 18, borderBottom: `1px solid ${C.border}` }}>
          <div onClick={() => setAba("novo")} style={{ padding: "8px 16px", cursor: "pointer", color: aba === "novo" ? "#E8201A" : C.textMuted, fontWeight: 700, fontSize: 13, borderBottom: `2px solid ${aba === "novo" ? "#E8201A" : "transparent"}` }}>⚡ Novo reajuste</div>
          <div onClick={() => setAba("historico")} style={{ padding: "8px 16px", cursor: "pointer", color: aba === "historico" ? "#E8201A" : C.textMuted, fontWeight: 700, fontSize: 13, borderBottom: `2px solid ${aba === "historico" ? "#E8201A" : "transparent"}` }}>📜 Histórico</div>
        </div>

        {/* ─────────── ABA: NOVO ─────────── */}
        {aba === "novo" && (
          <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>

            {/* STEPPER */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, fontSize: 12 }}>
              {[1, 2, 3].map(s => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: step >= s ? "#E8201A" : C.border, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>{s}</div>
                  <div style={{ color: step >= s ? C.text : C.textMuted, fontWeight: 700 }}>
                    {s === 1 ? "Filtro" : s === 2 ? "Reajuste" : "Preview"}
                  </div>
                  {s < 3 && <div style={{ width: 30, height: 2, background: step > s ? "#E8201A" : C.border, marginLeft: 4 }} />}
                </div>
              ))}
            </div>

            {/* PASSO 1: FILTRO */}
            {step === 1 && (
              <div>
                <label style={sLabel}>Escopo do reajuste</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8, marginBottom: 18 }}>
                  {[
                    { v: "todos", l: "Todos", e: "🌐" },
                    { v: "categoria", l: "Categoria", e: "📂" },
                    { v: "subcategoria", l: "Subcategoria", e: "📁" },
                    { v: "faixa", l: "Faixa preço", e: "💰" },
                    { v: "manual", l: "Manual", e: "✋" },
                  ].map(opt => (
                    <div key={opt.v} onClick={() => setEscopoTipo(opt.v)} style={{ ...sChip(escopoTipo === opt.v, "#3B82F6"), padding: "12px 6px", textAlign: "center" }}>
                      <div style={{ fontSize: 22 }}>{opt.e}</div>
                      <div style={{ fontSize: 11, marginTop: 4 }}>{opt.l}</div>
                    </div>
                  ))}
                </div>

                {escopoTipo === "categoria" && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={sLabel}>Selecione a categoria</label>
                    <select value={categoriaId} onChange={e => setCategoriaId(e.target.value)} style={sInput}>
                      <option value="">— escolha —</option>
                      {(categorias || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  </div>
                )}

                {escopoTipo === "subcategoria" && (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <label style={sLabel}>Categoria pai</label>
                      <select value={categoriaId} onChange={e => { setCategoriaId(e.target.value); setSubcategoriaId(""); }} style={sInput}>
                        <option value="">— escolha —</option>
                        {(categorias || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                      </select>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={sLabel}>Subcategoria</label>
                      <select value={subcategoriaId} onChange={e => setSubcategoriaId(e.target.value)} style={sInput} disabled={!categoriaId}>
                        <option value="">— escolha —</option>
                        {subsDaCategoria.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                      </select>
                    </div>
                  </>
                )}

                {escopoTipo === "faixa" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                    <div>
                      <label style={sLabel}>Preço mínimo (R$)</label>
                      <input value={precoMin} onChange={e => setPrecoMin(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="0,00" style={sInput} inputMode="decimal" />
                    </div>
                    <div>
                      <label style={sLabel}>Preço máximo (R$)</label>
                      <input value={precoMax} onChange={e => setPrecoMax(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="∞" style={sInput} inputMode="decimal" />
                    </div>
                  </div>
                )}

                {escopoTipo === "manual" && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={sLabel}>Selecione os produtos ({idsSelecionados.length} selecionado{idsSelecionados.length !== 1 ? "s" : ""})</label>
                    <input value={buscaManual} onChange={e => setBuscaManual(e.target.value)} placeholder="🔎 Buscar produto..." style={{ ...sInput, marginBottom: 8 }} />
                    <div style={{ maxHeight: 260, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg }}>
                      {produtosDisponiveisManual.map(p => {
                        const sel = idsSelecionados.includes(p.id);
                        return (
                          <div key={p.id} onClick={() => setIdsSelecionados(s => sel ? s.filter(i => i !== p.id) : [...s, p.id])} style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", background: sel ? "#3B82F620" : "transparent", borderBottom: `1px solid ${C.border}` }}>
                            <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${sel ? "#3B82F6" : C.border}`, background: sel ? "#3B82F6" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 900 }}>{sel ? "✓" : ""}</div>
                            <div style={{ flex: 1, fontSize: 13 }}>{p.nome}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#22C55E", fontFamily: "monospace" }}>{fmt(p.preco)}</div>
                          </div>
                        );
                      })}
                      {produtosDisponiveisManual.length === 0 && <div style={{ padding: 24, textAlign: "center", color: C.textMuted, fontSize: 13 }}>Nenhum produto encontrado</div>}
                    </div>
                  </div>
                )}

                {/* RESUMO */}
                <div style={{ padding: 14, background: "#3B82F610", border: `1px solid #3B82F640`, borderRadius: 10, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: C.textMuted, fontWeight: 700 }}>Produtos selecionados:</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: "#3B82F6", marginTop: 4 }}>{produtosFiltrados.length}</div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{escopoDescricao}</div>
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => onFechar && onFechar()} style={sBtnGhost}>Cancelar</button>
                  <button onClick={() => setStep(2)} disabled={produtosFiltrados.length === 0} style={{ ...sBtn("#3B82F6"), opacity: produtosFiltrados.length === 0 ? 0.5 : 1, cursor: produtosFiltrados.length === 0 ? "not-allowed" : "pointer" }}>Avançar →</button>
                </div>
              </div>
            )}

            {/* PASSO 2: REAJUSTE */}
            {step === 2 && (
              <div>
                <label style={sLabel}>Tipo de reajuste</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <div onClick={() => setTipo("percentual")} style={sChip(tipo === "percentual", "#22C55E")}>% Percentual</div>
                  <div onClick={() => setTipo("fixo")} style={sChip(tipo === "fixo", "#F97316")}>R$ Valor fixo</div>
                </div>

                <label style={sLabel}>Operação</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <div onClick={() => setOperacao("+")} style={sChip(operacao === "+", "#22C55E")}>＋ Aumentar</div>
                  <div onClick={() => setOperacao("-")} style={sChip(operacao === "-", "#EF4444")}>－ Diminuir</div>
                </div>

                <label style={sLabel}>Valor {tipo === "percentual" ? "(%)" : "(R$)"}</label>
                <input value={valor} onChange={e => setValor(e.target.value.replace(/[^\d.,]/g, ""))} placeholder={tipo === "percentual" ? "Ex: 10" : "Ex: 2,00"} style={{ ...sInput, fontSize: 22, fontWeight: 900, textAlign: "center", fontFamily: "monospace", marginBottom: 18, padding: "16px 12px" }} inputMode="decimal" autoFocus />

                <label style={sLabel}>Aplicar em</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <div onClick={() => setAplicarEm("preco")} style={sChip(aplicarEm === "preco", "#3B82F6")}>Preço normal</div>
                  <div onClick={() => setAplicarEm("promocao")} style={sChip(aplicarEm === "promocao", "#3B82F6")}>Preço promocional</div>
                  <div onClick={() => setAplicarEm("ambos")} style={sChip(aplicarEm === "ambos", "#3B82F6")}>Ambos</div>
                </div>

                <label style={sLabel}>Arredondamento</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 6, marginBottom: 18 }}>
                  {[
                    { v: "none", l: "Sem", d: "R$ 14,38" },
                    { v: "50", l: ",50", d: "R$ 14,50" },
                    { v: "90", l: ",90", d: "R$ 14,90" },
                    { v: "99", l: ",99", d: "R$ 14,99" },
                    { v: "int", l: "Inteiro", d: "R$ 14" },
                  ].map(o => (
                    <div key={o.v} onClick={() => setArredondamento(o.v)} style={{ ...sChip(arredondamento === o.v, "#8B5CF6"), padding: "10px 4px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, fontWeight: 800 }}>{o.l}</div>
                      <div style={{ fontSize: 9, marginTop: 2, opacity: 0.7 }}>{o.d}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
                  <button onClick={() => setStep(1)} style={sBtnGhost}>← Voltar</button>
                  <button onClick={irPreview} disabled={pvFloat(valor) <= 0 || carregando} style={{ ...sBtn("#3B82F6"), opacity: pvFloat(valor) <= 0 ? 0.5 : 1 }}>{carregando ? "Calculando..." : "Ver Preview →"}</button>
                </div>
              </div>
            )}

            {/* PASSO 3: PREVIEW */}
            {step === 3 && preview && (
              <div>
                <div style={{ padding: 14, background: "#F9731610", border: `1px solid #F9731640`, borderRadius: 10, marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 700, textTransform: "uppercase" }}>Confirme antes de aplicar</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginTop: 4 }}>{escopoDescricao}</div>
                  <div style={{ fontSize: 13, color: "#F97316", fontWeight: 700, marginTop: 2 }}>
                    {operacao}{valor}{tipo === "percentual" ? "%" : " R$"} em <b>{aplicarEm === "preco" ? "preço normal" : aplicarEm === "promocao" ? "preço promocional" : "ambos preços"}</b>
                    {arredondamento !== "none" && ` · arredondar pra ${arredondamento === "int" ? "inteiro" : "," + arredondamento}`}
                  </div>
                </div>

                <div style={{ maxHeight: 320, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 14 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: C.bg }}>
                        <th style={{ padding: 10, textAlign: "left", borderBottom: `1px solid ${C.border}`, fontWeight: 700, color: C.textMuted, fontSize: 11 }}>Produto</th>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontWeight: 700, color: C.textMuted, fontSize: 11 }}>Antes</th>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontWeight: 700, color: C.textMuted, fontSize: 11 }}>Depois</th>
                        <th style={{ padding: 10, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontWeight: 700, color: C.textMuted, fontSize: 11 }}>Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map(p => {
                        const delta = p.preco_depois - p.preco_antes;
                        const corDelta = delta > 0 ? "#22C55E" : delta < 0 ? "#EF4444" : C.textMuted;
                        return (
                          <tr key={p.id} style={{ background: p.mudou ? "transparent" : C.bg + "80" }}>
                            <td style={{ padding: 10, borderBottom: `1px solid ${C.border}`, color: p.mudou ? C.text : C.textMuted }}>
                              {p.nome}
                              {!p.mudou && <span style={{ marginLeft: 6, fontSize: 10, color: C.textMuted }}>(sem mudança)</span>}
                            </td>
                            <td style={{ padding: 10, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontFamily: "monospace", color: C.textMuted, textDecoration: p.mudou ? "line-through" : "none" }}>{fmt(p.preco_antes)}</td>
                            <td style={{ padding: 10, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontFamily: "monospace", fontWeight: 800, color: p.mudou ? "#22C55E" : C.textMuted }}>{fmt(p.preco_depois)}</td>
                            <td style={{ padding: 10, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontFamily: "monospace", color: corDelta, fontWeight: 700 }}>{delta > 0 ? "+" : ""}{fmt(delta)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
                  <button onClick={() => setStep(2)} style={sBtnGhost} disabled={aplicando}>← Voltar</button>
                  <button onClick={aplicar} disabled={aplicando} style={{ ...sBtn("#22C55E"), padding: "12px 22px", opacity: aplicando ? 0.6 : 1 }}>
                    {aplicando ? "Aplicando..." : `✓ Aplicar em ${preview.filter(p => p.mudou).length} produto(s)`}
                  </button>
                </div>
              </div>
            )}

          </div>
        )}

        {/* ─────────── ABA: HISTÓRICO ─────────── */}
        {aba === "historico" && (
          <div style={{ flex: 1, overflowY: "auto" }}>
            {carregandoHist && <div style={{ padding: 30, textAlign: "center", color: C.textMuted }}>Carregando...</div>}
            {!carregandoHist && historico.length === 0 && <div style={{ padding: 30, textAlign: "center", color: C.textMuted }}>Nenhum reajuste registrado ainda</div>}
            {!carregandoHist && historico.map(h => (
              <div key={h.id} style={{ padding: 14, background: C.bg, borderRadius: 10, marginBottom: 10, border: `1px solid ${C.border}`, opacity: h.revertido == 1 ? 0.6 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{h.escopo_descricao}</div>
                    <div style={{ fontSize: 12, color: h.operacao === "+" ? "#22C55E" : "#EF4444", fontWeight: 700, marginTop: 4 }}>
                      {h.operacao}{h.valor}{h.tipo === "percentual" ? "%" : " R$"} em {h.aplicar_em === "preco" ? "preço" : h.aplicar_em === "promocao" ? "promoção" : "ambos"}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                      {fmtData(h.criado_em)} · {h.operador_nome || "—"} · <b>{h.qtd_produtos} produtos</b>
                    </div>
                    {h.revertido == 1 && <div style={{ fontSize: 11, color: "#F97316", fontWeight: 700, marginTop: 4 }}>↩ Revertido em {fmtData(h.revertido_em)}</div>}
                  </div>
                  {h.revertido != 1 && (
                    <button onClick={() => reverter(h.id)} style={{ ...sBtn("#F97316"), padding: "6px 12px", fontSize: 11 }}>↩ Reverter</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
